import { useState, useEffect, useMemo, useRef } from 'react';
import { TextField, MenuItem, Autocomplete, CircularProgress, Typography, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import useStore from '../config/store';
import { isTalanaRef, refName, toTalanaRef, normalizeName } from './talanaRefs';

/**
 * Select / autocomplete alimentado por un catálogo de Talana sincronizado
 * (`GET /erp/talana/catalogs/:type`). Reemplaza los enums propios y los textos
 * libres de la ficha (PLAN_INTEGRACION_TALANA §4.2).
 *
 * - Valor: referencia `{ uid, talanaId, name, code }` (ver talanaRefs.js) o
 *   string legado de fichas anteriores. `onChange(ref | '')`.
 * - Catálogos chicos → <Select>; grandes (o `remote`) → <Autocomplete>. Con
 *   `remote` la búsqueda se hace en el servidor (sucursales: 10k en sandbox).
 * - Valores legados: al cargar el catálogo se intenta el cruce por nombre /
 *   código y se sube a referencia automáticamente; si no hay par, el texto
 *   se muestra como opción marcada "sin catálogo" para que el BP elija.
 * - Inactivos ocultos salvo que sean el valor guardado.
 * - Catálogo vacío → campo deshabilitado con aviso "sincronizar en
 *   Configuración → Talana".
 *
 * Cache por organización + tipo + filtro `tipo`, compartido entre instancias
 * (la ficha monta varios selects del mismo catálogo).
 */

const cache = new Map(); // key → { items, promise }
const cacheKey = (organization, type, tipo) => `${organization}|${type}|${tipo || ''}`;

export const invalidateTalanaCatalogCache = () => cache.clear();

const fetchCatalog = async ({ organization, authToken, type, tipo, search, limit }) => {
  const params = { organizationId: organization, active: 'all' };
  if (tipo) params.tipo = tipo;
  if (search) params.search = search;
  if (limit) params.limit = limit;
  const response = await api.get(`/erp/talana/catalogs/${type}`, {
    params,
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (response.data.response?.status_response !== 'OK') return [];
  return response.data.response.data || [];
};

const loadCached = ({ organization, authToken, type, tipo }) => {
  const key = cacheKey(organization, type, tipo);
  const hit = cache.get(key);
  if (hit?.items) return Promise.resolve(hit.items);
  if (hit?.promise) return hit.promise;
  const promise = fetchCatalog({ organization, authToken, type, tipo })
    .then((items) => {
      cache.set(key, { items });
      return items;
    })
    .catch((err) => {
      cache.delete(key);
      throw err;
    });
  cache.set(key, { promise });
  return promise;
};

/**
 * Catálogo completo (cacheado) para lógica fuera del select — p. ej. la
 * coherencia comuna → ciudad de la ficha, que recorre `parent` del árbol de
 * ubicaciones. Devuelve la misma lista que usa el componente.
 */
export const getTalanaCatalog = ({ organization, authToken, type, tipo = null }) =>
  loadCached({ organization, authToken, type, tipo });

/** Cruce de un texto legado con el catálogo: nombre exacto, código, razón social, contiene */
const matchLegacy = (items, legacy) => {
  const needle = normalizeName(legacy);
  if (!needle) return null;
  return (
    items.find((i) => normalizeName(i.name) === needle)
    || items.find((i) => i.code && normalizeName(i.code) === needle)
    || items.find((i) => i.extra?.razonSocial && normalizeName(i.extra.razonSocial) === needle)
    || items.find((i) => normalizeName(i.name).includes(needle) || (i.extra?.razonSocial && normalizeName(i.extra.razonSocial).includes(needle)))
    || null
  );
};

const AUTOCOMPLETE_THRESHOLD = 25;

const ErpTalanaSelect = ({
  type,
  value,
  onChange,
  label,
  tipo = null,          // filtro servidor (ubicaciones: Comuna | Ciudad | Region)
  filter = null,        // filtro cliente (item) => boolean
  remote = false,       // búsqueda en servidor (catálogos enormes)
  refExtra = null,      // (item) => campos extra a guardar en la referencia
  helperText = null,
  required = false,
  disabled = false,
  size = 'small',
}) => {
  const { t } = useTranslation();
  const { organization, authToken } = useStore.getState();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const debounceRef = useRef(null);
  const upgradedRef = useRef(false);

  // Carga del catálogo (cacheada). En modo remote se carga solo lo que se busca.
  useEffect(() => {
    if (!organization || !type) return undefined;
    let cancelled = false;
    if (remote) {
      // Primer lote sin búsqueda para que el desplegable no esté vacío
      setLoading(true);
      fetchCatalog({ organization, authToken, type, tipo, limit: 50 })
        .then((data) => { if (!cancelled) setItems(data); })
        .catch(() => { if (!cancelled) setFailed(true); })
        .finally(() => { if (!cancelled) setLoading(false); });
      return () => { cancelled = true; };
    }
    setLoading(true);
    loadCached({ organization, authToken, type, tipo })
      .then((data) => { if (!cancelled) setItems(data); })
      .catch(() => { if (!cancelled) setFailed(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, type, tipo, remote]);

  // Búsqueda remota con debounce
  useEffect(() => {
    if (!remote || !organization) return undefined;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const data = await fetchCatalog({ organization, authToken, type, tipo, search: inputValue.trim() || undefined, limit: 50 });
        setItems(data);
      } catch (err) {
        setFailed(true);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, remote]);

  const visible = useMemo(() => {
    const selectedId = isTalanaRef(value) ? String(value.talanaId) : null;
    return items.filter((i) => {
      const isSelected = String(i.talanaId) === selectedId;
      return (isSelected || !filter || filter(i)) && (i.active !== false || isSelected);
    });
  }, [items, filter, value]);

  const isLegacy = typeof value === 'string' && value.trim() !== '';
  const legacyMatch = useMemo(() => (isLegacy ? matchLegacy(items, value) : null), [items, value, isLegacy]);

  // Valor legado con par en el catálogo → subir a referencia (una vez)
  useEffect(() => {
    if (isLegacy && legacyMatch && !upgradedRef.current) {
      upgradedRef.current = true;
      onChange(toTalanaRef(legacyMatch, refExtra ? refExtra(legacyMatch) : {}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legacyMatch, isLegacy]);

  const empty = !loading && !failed && items.length === 0 && !remote;
  const hint = failed
    ? t('erp.talanaSelect.loadError')
    : empty
      ? t('erp.talanaSelect.notSynced')
      : isLegacy && !legacyMatch && items.length > 0
        ? t('erp.talanaSelect.legacyHint')
        : helperText;

  // Solo el nombre: los códigos (SBIF del banco, CUT de la comuna) son internos
  // y viajan en la referencia, pero no le dicen nada a quien llena la ficha.
  const optionLabel = (item) => {
    if (!item) return '';
    if (item.__legacy) return `${item.name} (${t('erp.talanaSelect.legacy')})`;
    return item.name || '';
  };

  const emit = (item) => {
    if (!item) return onChange('');
    if (item.__legacy) return onChange(item.name);
    return onChange(toTalanaRef(item, refExtra ? refExtra(item) : {}));
  };

  const useAutocomplete = remote || visible.length > AUTOCOMPLETE_THRESHOLD;

  // ---- Autocomplete (catálogos grandes / remotos) ----
  if (useAutocomplete) {
    const selected = isTalanaRef(value)
      ? visible.find((i) => String(i.talanaId) === String(value.talanaId))
        || { uid: value.uid, talanaId: value.talanaId, name: value.name, code: value.code }
      : isLegacy
        ? { __legacy: true, name: value, talanaId: `legacy:${value}` }
        : null;
    const options = isLegacy && !legacyMatch ? [selected, ...visible] : visible;
    return (
      <Autocomplete
        size={size}
        options={options}
        value={selected}
        loading={loading}
        disabled={disabled || empty || failed}
        getOptionLabel={optionLabel}
        isOptionEqualToValue={(a, b) => String(a?.talanaId) === String(b?.talanaId)}
        onChange={(e, item) => emit(item)}
        onInputChange={(e, text, reason) => { if (reason === 'input') setInputValue(text); }}
        filterOptions={remote ? (x) => x : undefined}
        noOptionsText={t('erp.talanaSelect.noOptions')}
        renderOption={(props, item) => (
          <li {...props} key={item.uid || item.talanaId}>
            <Box>
              <Typography variant="body2">{item.__legacy ? `${item.name} (${t('erp.talanaSelect.legacy')})` : item.name}</Typography>
              {!item.__legacy && item.active === false && (
                <Typography variant="caption" color="text.secondary">{t('erp.common.inactive')}</Typography>
              )}
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            helperText={hint}
            error={Boolean(failed) || (isLegacy && !legacyMatch && items.length > 0)}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    );
  }

  // ---- Select (catálogos chicos) ----
  const selectedValue = isTalanaRef(value) ? String(value.talanaId) : isLegacy ? `legacy:${value}` : '';
  const hasSelectedInVisible = isTalanaRef(value) && visible.some((i) => String(i.talanaId) === selectedValue);
  return (
    <TextField
      select
      fullWidth
      size={size}
      label={label}
      required={required}
      value={selectedValue}
      disabled={disabled || empty || failed}
      helperText={hint}
      error={Boolean(failed) || (isLegacy && !legacyMatch && items.length > 0)}
      onChange={(e) => {
        const v = e.target.value;
        if (v === '') return emit(null);
        if (v.startsWith('legacy:')) return emit({ __legacy: true, name: v.slice(7) });
        return emit(visible.find((i) => String(i.talanaId) === v));
      }}
      InputProps={loading ? { endAdornment: <CircularProgress size={16} sx={{ mr: 3 }} /> } : undefined}
    >
      <MenuItem value="">{t('select')}</MenuItem>
      {isLegacy && !legacyMatch && (
        <MenuItem value={`legacy:${value}`} sx={{ color: 'warning.main' }}>
          {value} ({t('erp.talanaSelect.legacy')})
        </MenuItem>
      )}
      {isTalanaRef(value) && !hasSelectedInVisible && (
        <MenuItem value={selectedValue}>{refName(value)}</MenuItem>
      )}
      {visible.map((item) => (
        <MenuItem key={item.uid} value={String(item.talanaId)}>
          {item.name}
          {item.active === false && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {t('erp.common.inactive')}
            </Typography>
          )}
        </MenuItem>
      ))}
    </TextField>
  );
};

export default ErpTalanaSelect;
